import { Icon, List, ListItem, ListItemText, MenuItem, Popper, TextField } from '@mui/material';
import { fieldSettings } from '../../environment';
import React, { useRef, useState } from 'react';

interface ITextFieldInputProps {
    id: string;
    label: string;
    value: any;
    obj?: any;
    setValue: (value: any) => void;
    placeholder?: string;
    type?: React.HTMLInputTypeAttribute | undefined;
    maxLength?: number;
    disabled?: boolean | false;
    required?: boolean | false;
    error?: boolean | false;
    helperText?: string;
    autoFocus?: boolean | false;
    multiline?: boolean
    rows?: number | string;
    numericOnly?: boolean;
    mask?: string;
    readonly?: boolean;
    autoComplete?: string;
    valueType?: 'text' | 'currency' | undefined;
    transformCase?: 'uppercase' | 'lowercase';
    valueList?: any[];
    valueKey?: string;
    labelKey?: string;
    isUserFollowUp?: boolean | false
}
export const TextFieldInput: React.FC<ITextFieldInputProps> = ({
    id,
    label,
    value,
    obj,
    placeholder,
    setValue,
    maxLength,
    type,
    disabled,
    autoFocus,
    multiline,
    required,
    error,
    helperText,
    rows,
    numericOnly,
    mask,
    readonly,
    autoComplete,
    valueType,
    transformCase,
    valueList = [],
    valueKey = '',
    labelKey = '',
    isUserFollowUp = false
}) => {

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
    const inputRef = useRef<HTMLInputElement | null>(null);

    value = type === 'datetime-local' ? formatDateTimeLocalValue(value) : value ?? '';

    const handleSelectOption = (option: any) => {
        const lastAtIndex = value.lastIndexOf('@');
        if (lastAtIndex !== -1) {
            const newValue = value.substring(0, lastAtIndex + 1) + option[labelKey] + ' ' + value.substring(lastAtIndex + 1);

            const existingEmails = obj?.emails ? obj.emails.split(';') : [];

            if (!existingEmails.includes(option[valueKey])) {
                existingEmails.push(option[valueKey]);
            }

            setValue(obj ? { ...obj, [id]: newValue, emails: existingEmails.join(';') } : { [id]: newValue, emails: existingEmails.join(';') });
        }
        setAnchorEl(null);
    };


    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, atValue?: any) => {
        let newValue = atValue ?? event.target.value ?? '';

        if (transformCase === 'uppercase') {
            newValue = newValue.toUpperCase();
        } else if (transformCase === 'lowercase') {
            newValue = newValue.toLowerCase();
        }

        if (numericOnly) {
            newValue = newValue.replace(/\D/g, '');
        }

        if (mask && newValue.length > 0) {
            const maskedValue = applyMask(newValue, mask);
            newValue = maskedValue;
        }

        if (valueType === 'currency') {
            const currency = formatCurrency(newValue);
            newValue = currency;
        }

        if (!readonly) {
            setValue(obj ? { ...obj, [id]: newValue } : newValue);
        }

        if (!newValue) {
            return;
        }
        if (isUserFollowUp) {
            const match = newValue.match(/@(\w*)$/);
            if (match) {
                const query = match[1].toLowerCase();
                setFilteredOptions(valueList.filter(option => option[labelKey].toLowerCase().includes(query)));
                setAnchorEl(inputRef.current);
            } else {
                setAnchorEl(null);
            }
        }
    };



    return (
        <React.Fragment>
            <TextField
                fullWidth
                id={id}
                label={label}
                placeholder={placeholder ?? placeholder}
                name={id}
                size='small'
                value={value}
                variant={fieldSettings.fieldVariant}
                onChange={handleChange}
                type={type ?? type}
                disabled={disabled}
                autoFocus={autoFocus}
                multiline={multiline ?? multiline}
                required={required}
                autoComplete={autoComplete}
                error={error}
                helperText={helperText}
                rows={rows ?? rows}
                inputProps={{
                    maxLength
                }}
                inputRef={inputRef}
            />
            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                disablePortal={false}
                modifiers={[
                    {
                        name: 'zIndex',
                        enabled: true,
                        phase: 'afterWrite',
                        fn: ({ state }) => {
                            state.styles.popper.zIndex = '9999';
                        },
                    },
                ]}
                sx={{ backgroundColor: 'gray', borderRadius: '4px', boxShadow: 3, padding: '8px', minWidth: '200px' }}
            >
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => (
                        <MenuItem
                            key={index}
                            value={option.id}
                            sx={{ fontSize: 14, color: 'white' }}
                            onClick={() => handleSelectOption(option)}
                        >
                            {option[labelKey]}
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled sx={{ fontSize: 14, color: 'white' }}>
                        Nenhum resultado encontrado
                    </MenuItem>
                )}
            </Popper>




        </React.Fragment>
    );
};


function applyMask(value: string, mask: string) {
    let maskedValue = '';
    let valueIndex = 0;

    for (let i = 0; i < mask.length; i++) {
        if (mask[i] === '0') {
            if (valueIndex < value.length) {
                maskedValue += value[valueIndex];
                valueIndex++;
            }
        } else {
            maskedValue += mask[i];
        }
    }

    return maskedValue;
}

function formatCurrency(value: string) {
    const numberValue = parseInt(value) / 100; // Considera os centavos para formatação
    const formatter = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });

    return formatter.format(numberValue);
}

function formatDateTimeLocalValue(value: any) {
    if (!value) {
        return '';
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
        return value.substring(0, 16);
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 16);
}
